import { Box, Button, ButtonGroup, Paper, Typography, Chip, Divider } from '@mui/material';
import FinancialSummary from '../FinancialSummary';
import PayoffChart from '..';
import { PositionWithExpiryData } from '../../OptionChain/types';
import { OptionChainWithExpiryType } from 'app/pages/Simulator';
import { useState } from 'react';
import PositionGreeksTable from '../GreeksTable/PositionGreeksTable';
import CommonMessage from '../CommonMessage';
import { convertPositionsMetaData } from '../../OptionChain/PositionTab/PositionTab';
import { getMTM } from '../../OptionChain/utils';
import { useTheme } from '@mui/material/styles';
import { FutureDataType } from 'types/ApiServicesTypes';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import FunctionsIcon from '@mui/icons-material/Functions';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

type OptionPayoffProps = {
  maxProfitLoss: {
    max_profit: string | number;
    max_loss: string | number;
    breakevenPoints: number[];
  };

  setMaxProfitLoss: (value: {
    max_profit: string | number;
    max_loss: string | number;
    breakevenPoints: number[];
  }) => void;
  date: string;
  optionChainDatawithExpiry: OptionChainWithExpiryType;
  position: PositionWithExpiryData;
  lotSize: number;
  spot_price: number | undefined;
  futureData: Record<string, FutureDataType>;
};

const OptionPayoff = ({
  maxProfitLoss,
  setMaxProfitLoss,
  date,
  optionChainDatawithExpiry,
  position,
  lotSize,
  spot_price,
  futureData,
}: OptionPayoffProps) => {
  const [active, setActive] = useState('payoff');
  const theme = useTheme();

  const graphPosition = convertPositionsMetaData(position, date);
  const filterredGraphPosition = graphPosition.filter(item => item.isActive);

  let totalMTM = 0;
  graphPosition.forEach(position => {
    let current_ltp = 0;
    const current_ltp_opt =
      optionChainDatawithExpiry &&
      optionChainDatawithExpiry[`${position.expiryDate}`];
    const current_ltp_pos = 
      current_ltp_opt &&
      current_ltp_opt.option &&
      current_ltp_opt.option[`${position.strike}`];
    if (position.contractType === 'fut') {
      current_ltp =
        current_ltp_opt &&
        current_ltp_opt.future &&
        current_ltp_opt.future[`${position.strike}`] && current_ltp_opt.future[`${position.strike}`].close;
    }
    else if (position.contractType === 'ce') {
      current_ltp = current_ltp_pos?.CE.close || 0;
    } else {
      current_ltp = current_ltp_pos?.PE.close || 0;
    }
    totalMTM += getMTM(
      position.positionCandleWise && Object.values(position.positionCandleWise),
      lotSize,
      current_ltp,
    );
  });

  // Calculate quick stats
  const totalPositions = filterredGraphPosition.length;
  const callPositions = filterredGraphPosition.filter(p => p.contractType === 'ce').length;
  const putPositions = filterredGraphPosition.filter(p => p.contractType === 'pe').length;
  const futurePositions = filterredGraphPosition.filter(p => p.contractType === 'fut').length;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Compact Header Section */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px 12px 0 0',
          border: `1px solid ${theme.palette.divider}`,
          borderBottom: 'none',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} color="text.primary">
              Payoff Analysis
            </Typography>
            {totalPositions > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Chip
                  label={`${totalPositions} Positions`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
                {callPositions > 0 && (
                  <Chip
                    icon={<TrendingUpIcon sx={{ fontSize: 12 }} />}
                    label={`${callPositions} Calls`}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: '20px' }}
                  />
                )}
                {putPositions > 0 && (
                  <Chip
                    icon={<TrendingDownIcon sx={{ fontSize: 12 }} />}
                    label={`${putPositions} Puts`}
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: '20px' }}
                  />
                )}
                {futurePositions > 0 && (
                  <Chip
                    label={`${futurePositions} Futures`}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: '20px' }}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* Compact Tab Buttons */}
          <ButtonGroup
            size="small"
            disableElevation
            aria-label="analysis view selector"
            orientation="horizontal"
            sx={{
              '& .MuiButtonGroup-grouped': {
                border: `1px solid ${theme.palette.divider}`,
                minWidth: 80,
                py: 0.5,
                px: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8rem',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  transform: 'translateY(-1px)',
                },
                '&.active': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
              },
            }}
            className="toggle-button-group"
          >
            <Button
              onClick={() => setActive('payoff')}
              className={active === 'payoff' ? 'active' : ''}
              startIcon={<ShowChartIcon sx={{ fontSize: 16 }} />}
            >
              Payoff Chart
            </Button>
            <Button
              onClick={() => setActive('greeks')}
              className={active === 'greeks' ? 'active' : ''}
              startIcon={<FunctionsIcon sx={{ fontSize: 16 }} />}
            >
              Greeks
            </Button>
          </ButtonGroup>
        </Box>

        {/* Compact Financial Summary */}
        {filterredGraphPosition.length > 0 && (
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <Divider sx={{ mb: 1 }} />
            <FinancialSummary
              optionChainData={optionChainDatawithExpiry}
              maxProfitLoss={maxProfitLoss}
              positionData={filterredGraphPosition}
              totalMTM={totalMTM}
              lotSize={lotSize}
            />
          </Box>
        )}
      </Paper>

      {/* Compact Content Area */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          borderRadius: '0 0 12px 12px',
          border: `1px solid ${theme.palette.divider}`,
          borderTop: 'none',
          overflow: 'hidden',
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, p: 1.5, overflow: 'hidden' }}>
          {active === 'payoff' ? (
            filterredGraphPosition.length > 0 ? (
              <Box
                sx={{
                  height: '100%',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <PayoffChart
                  date={date}
                  setMaxProfitLoss={setMaxProfitLoss}
                  positions={filterredGraphPosition}
                  lotSize={lotSize}
                  spot_price={spot_price}
                  futureData={futureData}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CommonMessage
                  title="Check out Payoff Graphs, Statistics, and additional details..."
                  desc1="Choose trades from the Option Chain or utilize a"
                  desc2="Prebuilt strategy from the Positions tab to see the analysis."
                />
              </Box>
            )
          ) : filterredGraphPosition.length > 0 ? (
            <Box
              sx={{
                height: '100%',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <PositionGreeksTable positions={filterredGraphPosition} />
            </Box>
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.background.paper,
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CommonMessage
                title="Greeks Analysis Available"
                desc1="Add positions to view detailed Greeks analysis"
                desc2="including Delta, Gamma, Theta, and Vega calculations."
              />
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default OptionPayoff;
